// Per-tab memory for one v2 package instance: the idempotency key and the ids of
// uploads staged so far. Nothing else — never answers, names, or previews (PHI).
// sessionStorage can throw (private mode, blocked storage); every access is guarded.

const PREFIX = 'operaitor_fpv2:';

export interface V2SessionRecord {
  v: 1;
  idempotency_key: string;
  staged_upload_ids: string[];
}

/** Satisfies the backend pattern ^[A-Za-z0-9._:~-]{8,200}$ on every path. */
export function newIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  try {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function storageKey(packageInstanceId: string): string {
  return `${PREFIX}${packageInstanceId}`;
}

function read(packageInstanceId: string): V2SessionRecord | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(packageInstanceId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<V2SessionRecord>;
    if (parsed.v !== 1 || typeof parsed.idempotency_key !== 'string') return null;
    return {
      v: 1,
      idempotency_key: parsed.idempotency_key,
      staged_upload_ids: Array.isArray(parsed.staged_upload_ids)
        ? parsed.staged_upload_ids.filter((id): id is string => typeof id === 'string')
        : [],
    };
  } catch {
    return null;
  }
}

function write(packageInstanceId: string, record: V2SessionRecord): void {
  try {
    window.sessionStorage.setItem(storageKey(packageInstanceId), JSON.stringify(record));
  } catch { /* storage unavailable: in-memory only for this tab */ }
}

export function loadOrCreateV2Session(packageInstanceId: string): V2SessionRecord {
  const existing = read(packageInstanceId);
  if (existing) return existing;
  const created: V2SessionRecord = { v: 1, idempotency_key: newIdempotencyKey(), staged_upload_ids: [] };
  write(packageInstanceId, created);
  return created;
}

export function recordStagedUpload(packageInstanceId: string, uploadId: string): V2SessionRecord {
  const record = loadOrCreateV2Session(packageInstanceId);
  if (record.staged_upload_ids.indexOf(uploadId) === -1) {
    record.staged_upload_ids = record.staged_upload_ids.concat(uploadId);
    write(packageInstanceId, record);
  }
  return record;
}

export function clearV2Session(packageInstanceId: string): void {
  try {
    window.sessionStorage.removeItem(storageKey(packageInstanceId));
  } catch { /* nothing to clear */ }
}
